import React from 'react';
import { ChevronRight } from 'lucide-react';
import { formatRelativeTime, formatDuration } from '../../../shared/utils/dateUtils';
import { AVAILABLE_COLUMNS } from '../config/userColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const UserRow = ({ user, onRowClick, columns, styles: stylesProp }) => {
  const styles = stylesProp || TABLE_STYLES;

  const getCellValue = (columnKey) => {
    const def = AVAILABLE_COLUMNS[columnKey];
    const field = def?.field || columnKey;

    switch (field) {
      case 'distinctId': {
        const raw = user.distinctId || user.userId;
        if (!raw) return '—';
        const s = String(raw);
        if (s.length <= 12) return s;
        return `${s.slice(0, 5)}...${s.slice(-5)}`;
      }
      case 'firstVisit':
        if (!user.firstVisit) return 'N/A';
        return formatRelativeTime(new Date(user.firstVisit));
      case 'lastVisitTime':
      case 'lastSession':
        if (!user.lastVisitTime && !user.lastSession) return 'N/A';
        return formatRelativeTime(new Date(user.lastVisitTime || user.lastSession));
      case 'totalPageViews':
        return user.totalPageViews != null ? String(user.totalPageViews) : 'N/A';
      case 'isWeb3User':
        return user.isWeb3User ? 'Yes' : 'No';
      case 'isBounce':
        return user.isBounce ? 'Yes' : 'No';
      case 'firstLocationCity':
      case 'lastLocationCity':
      case 'firstLocationCountry':
      case 'lastLocationCountry':
      case 'firstReferrer':
      case 'lastReferrer':
      case 'deviceType':
      case 'browserName':
      case 'os': {
        const val = user[field];
        return val != null && val !== '' ? String(val) : '—';
      }
      case 'primaryWalletAddress': {
        const raw = user.primaryWalletAddress;
        if (!raw || raw === '') return '—';
        const s = String(raw);
        if (s.length <= 12) return s;
        return `${s.slice(0, 5)}...${s.slice(-5)}`;
      }
      case 'totalTimeSpent':
        if (user.totalTimeSpent == null) return 'N/A';
        return formatDuration(user.totalTimeSpent);
      case 'networthUsd':
        if (user.networthUsd == null) return 'N/A';
        return `$${user.networthUsd.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
      default:
        return user[field] ?? '—';
    }
  };

  const getAlignmentClass = (columnKey) => {
    const def = AVAILABLE_COLUMNS[columnKey];
    const field = def?.field || columnKey;
    return field === 'distinctId' ? 'text-left' : 'text-center';
  };

  const displayColumns = columns && columns.length > 0 ? columns : ['distinct_id', 'is_web3_user', 'total_time_spent', 'last_visit_time'];
  const userId = user.distinctId || user.userId;

  return (
    <tr
      onClick={() => onRowClick && onRowClick(user)}
      className="cursor-pointer transition-colors"
      style={{
        borderBottom: `1px solid ${styles.borderColor}`,
        backgroundColor: 'transparent'
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.secondaryBg)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      {displayColumns.map((columnKey) => {
        const alignmentClass = getAlignmentClass(columnKey);
        const value = getCellValue(columnKey);
        return (
          <td key={columnKey} className={`px-6 py-3 whitespace-nowrap ${alignmentClass}`}>
            <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
              <span title={typeof value === 'string' ? value : ''}>{value || '—'}</span>
            </div>
          </td>
        );
      })}
      <td className="px-6 py-3 whitespace-nowrap text-right">
        <ChevronRight className="h-4 w-4 inline" style={{ color: styles.textSecondary }} />
      </td>
    </tr>
  );
};

export default UserRow;
